import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaDownload,
  FaQrcode,
  FaSearch,
  FaSpinner,
  FaTimes,
  FaUsers,
} from 'react-icons/fa';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  EmptyStateTitle,
  Select,
} from '../../styles/AdminStyles';
import {
  BRANCHES,
  downloadDataUrl,
  fetchAdminJson,
  getAdminHeaders,
  REGISTRATIONS_PER_PAGE,
} from './campAdminShared';

const CampQrCodesPage = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBranch, setFilterBranch] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [qrBundle, setQrBundle] = useState(null);
  const [loadingQrBundle, setLoadingQrBundle] = useState(false);

  useEffect(() => {
    const loadRegistrations = async () => {
      try {
        setLoading(true);
        const data = await fetchAdminJson('/camp-registration/all', {
          headers: getAdminHeaders(),
        });
        setRegistrations(data);
      } catch (loadError) {
        console.error('Error loading registrations:', loadError);
        setError(loadError.message || 'Failed to load registrations.');
      } finally {
        setLoading(false);
      }
    };

    loadRegistrations();
  }, []);

  const filteredRegistrations = useMemo(() => {
    return registrations.filter((registration) => {
      const searchableValues = [
        registration.fullName,
        registration.phone,
        registration.registrationNumber,
        registration.spouseInfo?.name,
        ...(registration.children || []).map((child) => child?.name),
      ]
        .filter(Boolean)
        .map((value) => value.toLowerCase());

      const matchesSearch =
        !searchTerm.trim() ||
        searchableValues.some((value) =>
          value.includes(searchTerm.toLowerCase()),
        );
      const matchesBranch =
        filterBranch === 'all' || registration.branch === filterBranch;

      return matchesSearch && matchesBranch;
    });
  }, [registrations, searchTerm, filterBranch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterBranch]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRegistrations.length / REGISTRATIONS_PER_PAGE),
  );

  const paginatedRegistrations = filteredRegistrations.slice(
    (currentPage - 1) * REGISTRATIONS_PER_PAGE,
    currentPage * REGISTRATIONS_PER_PAGE,
  );

  const loadQrCodes = async (registration) => {
    try {
      setSelectedRegistration(registration);
      setLoadingQrBundle(true);
      setQrBundle(null);
      const data = await fetchAdminJson(
        `/camp-registration/registration/${registration._id}/qr-codes`,
        {
          headers: getAdminHeaders(),
        },
      );
      setQrBundle(data);
    } catch (loadError) {
      console.error('Error loading QR codes:', loadError);
      setError(loadError.message || 'Failed to load camper QR codes.');
      setSelectedRegistration(null);
    } finally {
      setLoadingQrBundle(false);
    }
  };

  const downloadQrCode = (qrCode) => {
    if (!selectedRegistration || !qrCode?.qrCodeDataUrl) return;
    downloadDataUrl(
      qrCode.qrCodeDataUrl,
      `${selectedRegistration.registrationNumber}-${qrCode.roleLabel
        .toLowerCase()
        .replace(/\s+/g, '-')}.png`,
    );
  };

  const downloadAllQrCodes = () => {
    if (!qrBundle?.qrCodes?.length) return;
    qrBundle.qrCodes.forEach((qrCode) => downloadQrCode(qrCode));
  };

  if (loading) {
    return (
      <AdminLayout title="Camper QR Codes" subtitle="Loading...">
        <LoadingState>
          <FaSpinner className="spin" />
          <p>Loading camper registrations...</p>
        </LoadingState>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Generate Camper QR Codes"
      subtitle="Search for a camper registration and generate or download the QR codes for everyone on that registration."
    >
      {error && (
        <Alert $type="error" style={{ marginBottom: '1.5rem' }}>
          {error}
        </Alert>
      )}

      <Card style={{ marginBottom: '1.5rem' }}>
        <CardBody>
          <InfoPanel>
            <FaQrcode />
            <div>
              <strong>QR codes are generated here on demand</strong>
              <p>
                Open any registration below to create or load QR codes for the
                registrant, spouse, and children, then download them for camp
                check-in.
              </p>
            </div>
          </InfoPanel>
        </CardBody>
      </Card>

      <Card style={{ marginBottom: '1.5rem' }}>
        <CardBody>
          <Toolbar>
            <SearchBox>
              <FaSearch />
              <input
                type="text"
                placeholder="Search by name, phone, or registration number..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </SearchBox>

            <Select
              value={filterBranch}
              onChange={(event) => setFilterBranch(event.target.value)}
              style={{ minWidth: '190px' }}
            >
              <option value="all">All Branches</option>
              {BRANCHES.map((branch) => (
                <option key={branch} value={branch}>
                  {branch}
                </option>
              ))}
            </Select>
          </Toolbar>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            <FaQrcode /> Registrations Ready for QR Generation
          </CardTitle>
          <CountBadge>{filteredRegistrations.length} forms</CountBadge>
        </CardHeader>
        <CardBody>
          {paginatedRegistrations.length > 0 ? (
            <>
              <RegistrationGrid>
                {paginatedRegistrations.map((registration) => (
                  <RegistrationCard key={registration._id}>
                    <RegistrationTopRow>
                      <div>
                        <RegistrationName>{registration.fullName}</RegistrationName>
                        <RegistrationMeta>
                          {registration.registrationNumber}
                        </RegistrationMeta>
                      </div>
                      <ParticipantsBadge>
                        <FaUsers />{' '}
                        {1 +
                          (registration.hasSpouse && registration.spouseInfo?.name ? 1 : 0) +
                          (registration.children?.length || 0)}
                      </ParticipantsBadge>
                    </RegistrationTopRow>

                    <RegistrationMeta>{registration.branch || 'No branch'}</RegistrationMeta>
                    <RegistrationMeta>{registration.phone || 'No phone'}</RegistrationMeta>
                    <RegistrationMeta>{registration.roomType || 'No room assigned'}</RegistrationMeta>

                    <ButtonRow>
                      <Button onClick={() => loadQrCodes(registration)}>
                        <FaQrcode /> Generate QR Codes
                      </Button>
                    </ButtonRow>
                  </RegistrationCard>
                ))}
              </RegistrationGrid>

              {totalPages > 1 && (
                <PaginationRow>
                  <Button
                    $variant="secondary"
                    onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <PaginationText>
                    Page {currentPage} of {totalPages}
                  </PaginationText>
                  <Button
                    $variant="secondary"
                    onClick={() =>
                      setCurrentPage((page) => Math.min(page + 1, totalPages))
                    }
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </PaginationRow>
              )}
            </>
          ) : (
            <EmptyState>
              <EmptyStateIcon>QR</EmptyStateIcon>
              <EmptyStateTitle>No registrations found</EmptyStateTitle>
              <EmptyStateText>
                Try a different search term or branch filter.
              </EmptyStateText>
            </EmptyState>
          )}
        </CardBody>
      </Card>

      <AnimatePresence>
        {selectedRegistration && (
          <ModalOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedRegistration(null)}
          >
            <ModalContent
              as={motion.div}
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -30, opacity: 0 }}
              onClick={(event) => event.stopPropagation()}
            >
              <ModalHeader>
                <div>
                  <h2>Camper QR Codes</h2>
                  <p>
                    {selectedRegistration.fullName} •{' '}
                    {selectedRegistration.registrationNumber}
                  </p>
                </div>
                <CloseButton type="button" onClick={() => setSelectedRegistration(null)}>
                  <FaTimes />
                </CloseButton>
              </ModalHeader>

              <ModalBody>
                {loadingQrBundle ? (
                  <LoadingState>
                    <FaSpinner className="spin" />
                    <p>Generating camper QR codes...</p>
                  </LoadingState>
                ) : qrBundle?.qrCodes?.length ? (
                  <>
                    <ButtonRow style={{ justifyContent: 'flex-end', marginBottom: '1rem' }}>
                      <Button onClick={downloadAllQrCodes}>
                        <FaDownload /> Download All
                      </Button>
                    </ButtonRow>

                    <QrGrid>
                      {qrBundle.qrCodes.map((qrCode) => (
                        <QrCard key={qrCode.participantKey}>
                          <QrName>{qrCode.participantName}</QrName>
                          <QrRole>{qrCode.roleLabel}</QrRole>
                          <QrImage
                            src={qrCode.qrCodeDataUrl}
                            alt={`${qrCode.participantName} QR code`}
                          />
                          <Button onClick={() => downloadQrCode(qrCode)}>
                            <FaDownload /> Download QR
                          </Button>
                        </QrCard>
                      ))}
                    </QrGrid>
                  </>
                ) : (
                  <EmptyState>
                    <EmptyStateIcon>QR</EmptyStateIcon>
                    <EmptyStateTitle>No QR codes available</EmptyStateTitle>
                    <EmptyStateText>
                      This registration does not have any QR codes yet.
                    </EmptyStateText>
                  </EmptyState>
                )}
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </AdminLayout>
  );
};

const LoadingState = styled.div`
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: ${(props) => props.theme.colors.primary};

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
`;

const SearchBox = styled.div`
  flex: 1;
  min-width: 260px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border: 1px solid #dbe3ef;
  border-radius: 12px;
  background: white;

  svg {
    color: #94a3b8;
  }

  input {
    width: 100%;
    border: none;
    outline: none;
    font-size: 0.95rem;
  }
`;

const InfoPanel = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;

  svg {
    margin-top: 0.1rem;
    color: #1d4ed8;
    font-size: 1.4rem;
    flex-shrink: 0;
  }

  strong {
    display: block;
    color: #0f172a;
  }

  p {
    margin: 0.35rem 0 0;
    color: #64748b;
    line-height: 1.6;
  }
`;

const CountBadge = styled.div`
  padding: 0.55rem 0.85rem;
  border-radius: 999px;
  background: #0f172a;
  color: white;
  font-size: 0.82rem;
  font-weight: 700;
`;

const RegistrationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
`;

const RegistrationCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
`;

const RegistrationTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const RegistrationName = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 1rem;
`;

const RegistrationMeta = styled.div`
  color: #64748b;
  font-size: 0.88rem;
  margin-top: 0.35rem;
`;

const ParticipantsBadge = styled.div`
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.8rem;
  font-weight: 700;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  margin-top: 1.5rem;
  flex-wrap: wrap;
`;

const PaginationText = styled.div`
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 600;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.58);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 920px;
  max-height: 90vh;
  overflow: auto;
  background: white;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
`;

const ModalHeader = styled.div`
  padding: 1.4rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  border-bottom: 1px solid #e2e8f0;

  h2 {
    margin: 0;
    color: #0f172a;
    font-size: 1.2rem;
  }

  p {
    margin: 0.35rem 0 0;
    color: #64748b;
    font-size: 0.9rem;
  }
`;

const CloseButton = styled.button`
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const QrGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const QrCard = styled.div`
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  padding: 1rem;
  text-align: center;
`;

const QrName = styled.div`
  color: #0f172a;
  font-size: 1rem;
  font-weight: 800;
`;

const QrRole = styled.div`
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.85rem;
`;

const QrImage = styled.img`
  width: 190px;
  height: 190px;
  max-width: 100%;
  display: block;
  margin: 1rem auto;
  border-radius: 14px;
  background: white;
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
`;

export default CampQrCodesPage;
